import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import type {ParameterFieldType} from 'samples/plate-maps/PlateMapConstants';
import {getJSONPromise, patchJSONPromise, postJSONPromise} from 'util/Net';

const API_BASE = getApiBaseForObjectType(DomainGraphType.PLATE_RECORD);

// -- Shared nested types ---------------------------------------------------

export interface UnitInput {
  apiId: string;
  versionNumber?: number;
}

export interface QuantityInput {
  amount: number;
  unit?: UnitInput;
}

export interface ScratchPlateWellInput {
  rowIndex: number;
  columnIndex: number;
  role: string | null;
  subrole?: string;
  group?: string;
  sampleSchemaId?: string;
  schemaTags?: Record<string, unknown>;
  wellVolumeAmount?: number;
  wellVolumeUnitId?: string;
  wellVolumeUnitVersionNumber?: number;
}

export interface ConcentrationWellInput {
  wellPosition: string;
  concentrationAmount?: number | null;
}

export interface ConcentrationLayerInput {
  name: string;
  concentrationUnitId: string;
  concentrationUnitVersionNumber: number;
  wells?: Array<ConcentrationWellInput>;
}

export interface ScratchPlateMeasurementInput {
  rowIndex: number;
  columnIndex: number;
  measurementIdentifier?: string;
  measurementName?: string;
  measurementConcept?: string;
  measurementValue?: number;
  measurementUnit?: string;
  detectionType?: string;
  analyticalMethod?: string;
}

export interface ScratchPlateVendorFileInput {
  scratchFileId: string;
  vendor: string;
}

export interface ScratchPlatePlateReaderAsmInput {
  scratchFileId: string;
}

// A well placement specified by an entity, or by a container. When specified by container, every
// entity the container currently holds is placed (a container is not required to hold exactly one
// item); concentrationAmount/concentrationUnitId below apply as one shared value across all of them.
// Exactly one of entityId / containerId is provided.
type ContentItemIdentifier = {entityId: string; containerId?: null} | {containerId: string; entityId?: null};

export type ContentItemInput = ContentItemIdentifier & {
  wellPosition: string;
  rowIndex: number;
  columnIndex: number;
  contentLayerName?: string;
  concentrationAmount?: number;
  concentrationUnitId?: string;
  concentrationUnitVersionNumber?: number;
  transferQuantityAmount?: number;
  transferQuantityUnitId?: string;
  transferQuantityUnitVersionNumber?: number;
  // The test order(s) resolved for this placement's trial-sample entity when it was added as a
  // source (see PlateMapTestOrderUtils.ts) -- re-validated server-side, not trusted blindly.
  testOrderIds?: Array<string>;
};

export interface MetadataWellInput {
  wellPosition: string;
  value: string | number | boolean | null;
}

export type MetadataLayerApiType = 'text' | 'integer' | 'float' | 'boolean' | 'date' | 'datetime';

/**
 * One typed metadata column. Applies its values in one of two mutually-exclusive modes:
 * per-well via `wells` (sparse; omitted wells store null), or whole-plate via
 * `plateValue` (a single value duplicated onto every well, for plate-level metadata).
 */
export interface MetadataColumnInput {
  name: string;
  type: MetadataLayerApiType;
  wells?: Array<MetadataWellInput>;
  plateValue?: string | number | boolean | null;
  // Stable data-frame identity for a plate field (its reserved `plate_field_<uuid>` name). Round-trip
  // an existing plate field's system name to rename/retype it in place; omit for a new field so the
  // backend mints one. See MetadataColumnInput in scratch_plate_input_types.py.
  systemName?: string;
}

export interface ParameterWellInput {
  wellPosition: string;
  value: string | number | null;
}

/**
 * One procedure data-parameter layer. Mirrors the legacy plate-design parameter
 * layer: the layer references a `ProcedureParameterVersion` and carries a
 * per-well TEXT or FLOAT value.
 */
export interface ParameterLayerInput {
  parameterVersionId: string;
  name: string;
  fieldType: ParameterFieldType;
  wells: Array<ParameterWellInput>;
}

// -- CRUD types ------------------------------------------------------------

interface CreateScratchPlateBaseParams {
  authParentId: string;
  originDesignId?: string;
  name: string;
  height: number;
  width: number;
  plateSchemaId?: string;
  wells?: Array<ScratchPlateWellInput>;
  concentrationLayers?: Array<ConcentrationLayerInput>;
  metadataLayers?: Array<MetadataColumnInput>;
  parameterLayers?: Array<ParameterLayerInput>;
  measurements?: Array<ScratchPlateMeasurementInput>;
  contents?: Array<Array<ContentItemInput>>;
  // A create may instead describe a transfer, producing the destination plate(s) by
  // pulling content out of existing source plates.
  transfer?: ScratchPlateTransferInput;
}

export type CreateScratchPlateParams = CreateScratchPlateBaseParams &
  (
    | {vendorFile?: null; plateReaderAsm?: null}
    | {vendorFile: ScratchPlateVendorFileInput; plateReaderAsm?: null}
    | {vendorFile?: null; plateReaderAsm: ScratchPlatePlateReaderAsmInput}
  );

export interface CreateScratchPlatesResponse {
  createdIds: Array<string>;
}

export interface UpdateScratchPlateParams {
  name?: string;
  height?: number;
  width?: number;
  plateSchemaId?: string | null; // null = explicit removal
  wells?: Array<ScratchPlateWellInput>;
  concentrationLayers?: Array<ConcentrationLayerInput>;
  metadataLayers?: Array<MetadataColumnInput>;
  parameterLayers?: Array<ParameterLayerInput>;
}

export interface UpdateScratchPlatesResponse {
  updatedIds: Array<string>;
}

export interface ScratchPlateResponse {
  id: string;
  name: string;
  height: number;
  width: number;
  createdAt: string;
  modifiedAt: string;
}

export interface ListScratchPlatesParams {
  pageSize?: number;
  nextToken?: string;
}

export interface ListScratchPlatesResponse {
  items: Array<ScratchPlateResponse>;
  nextToken?: string;
}

export enum GridDirection {
  ACROSS_ROWS = 'ACROSS_ROWS',
  DOWN_COLUMNS = 'DOWN_COLUMNS',
}

// -- Transfer types --------------------------------------------------------

/**
 * A single well-to-well transfer into one of the destination plates a create
 * produces. The destination is addressed by `destinationPlateIndex` into the
 * created children (0 when a single destination plate is created). Source content
 * is never decremented — filled plates are immutable — so this records what moved.
 */
export interface WellTransferInput {
  sourcePlateId: string;
  sourceWellPosition: string;
  destinationPlateIndex: number;
  destinationWellPosition: string;
  transferQuantity?: QuantityInput;
}

/**
 * The transfer that fills a create's destination plate(s) out of existing sources.
 * Provide either explicit `operations` or a `layout` (with `sourcePlateIds`), never both.
 *
 * A layout consumes `sourcePlateIds` in order, grouped by its fan ratio: consolidation
 * layouts pack several sources into one destination (e.g. 12 sources -> 3 plates), while
 * distribution layouts split each source across several (e.g. N sources -> 4N plates). The
 * number of destination plates is inferred from the moves — the highest
 * `destinationPlateIndex` addressed — so the caller never specifies a count.
 */
export interface ScratchPlateTransferInput {
  operations?: Array<WellTransferInput>;
  sourcePlateIds?: Array<string>;
  layout?: PlateLayoutType;
  direction?: GridDirection;
  transferQuantity?: QuantityInput;
  skipEmpty?: boolean;
}

export enum PlateLayoutType {
  STAMP = 'STAMP',
  STAMP_TO_QUADRANTS = 'STAMP_TO_QUADRANTS',
  REVERSE_STAMP_TO_QUADRANTS = 'REVERSE_STAMP_TO_QUADRANTS',
  INTERLEAVE = 'INTERLEAVE',
  REVERSE_INTERLEAVE = 'REVERSE_INTERLEAVE',
  COLUMN_INTERLEAVE = 'COLUMN_INTERLEAVE',
  REVERSE_COLUMN_INTERLEAVE = 'REVERSE_COLUMN_INTERLEAVE',
}

// -- DAO functions ---------------------------------------------------------

export function createScratchPlates(
  creates: Array<CreateScratchPlateParams>
): Promise<CreateScratchPlatesResponse> {
  return postJSONPromise({url: API_BASE, data: {creates}});
}

export function updateScratchPlates(
  updates: Array<{id: string} & UpdateScratchPlateParams>
): Promise<UpdateScratchPlatesResponse> {
  return patchJSONPromise({url: API_BASE, data: {updates}});
}

/**
 * The design configuration plus per-record contents for creating an origin plate design and all its
 * record plates in one operation. All layout fields (wells, layers, schema) describe the origin
 * design; `contents` is a list-of-lists producing one record plate per inner list (or one empty
 * record plate when omitted). Unlike `CreateScratchPlateParams`, there is no `originDesignId` — the
 * origin design is created by this call.
 */
export type CreateScratchPlatesWithNewOriginDesignParams = Omit<
  CreateScratchPlateBaseParams,
  'originDesignId' | 'measurements' | 'transfer'
>;

export interface CreateScratchPlatesWithNewOriginDesignResponse {
  originDesignId: string;
  createdIds: Array<string>;
}

/**
 * Create a new origin plate design and all of its record plates in a single request. The record
 * plates are cloned from the newly-created design; `originDesignId` on each points back at it.
 */
export function createScratchPlatesWithNewOriginDesign(
  create: CreateScratchPlatesWithNewOriginDesignParams
): Promise<CreateScratchPlatesWithNewOriginDesignResponse> {
  return postJSONPromise({url: `${API_BASE}:create-with-origin-design`, data: {create}});
}

// -- Put in Inventory types ---------------------------------------------------

/**
 * Maps one scratch-plate layer column onto a container-schema field. Source
 * columns are qualified by `layerName` because the same column name can appear
 * in more than one layer; `schemaFieldName` is the target field's system name.
 */
export interface ScratchPlateColumnMapping {
  layerName: string;
  columnName: string;
  schemaFieldName: string;
}

/**
 * Maps one ad hoc plate-level metadata field onto a plate-schema field. Used when the scratch plate
 * has no plate schema of its own, so its plate-level values have no other way to line up with the
 * target schema's fields. `metadataFieldName` is the ad hoc field's display name (ad hoc fields have
 * no fixed system name); `metadataFieldType` is that field's type, disambiguating a display name that
 * collides across more than one type within a batch of plates (see getUnionPlateFields);
 * `schemaFieldName` is the target plate schema field's system name.
 */
export interface ScratchPlatePlateFieldMapping {
  metadataFieldName: string;
  metadataFieldType: LeafType;
  schemaFieldName: string;
}

export type BarcodeInput =
  | {barcodeMode: 'SPECIFY_BARCODE'; barcode: string}
  | {barcodeMode: 'USE_NAME'; useNameAsBarcode: boolean};

export interface PutScratchPlateInInventoryParams {
  scratchPlateId: string;
  plateSchemaId?: string;
  name?: string;
  barcode?: BarcodeInput;
  sourceId?: string;
  parentStorableId?: string;
  columnMappings?: Array<ScratchPlateColumnMapping>;
  plateFieldMappings?: Array<ScratchPlatePlateFieldMapping>;
  studyIds?: Array<string>;
}

export interface PutScratchPlateInInventoryResponse {
  plate: {id: string; name: string};
}

// -- Put in Inventory DAO function --------------------------------------------

export function putScratchPlateInInventory(
  params: PutScratchPlateInInventoryParams
): Promise<PutScratchPlateInInventoryResponse> {
  return postJSONPromise({
    url: `${API_BASE}:put-in-inventory`,
    data: {
      scratchPlateId: params.scratchPlateId,
      plateSchemaId: params.plateSchemaId ?? undefined,
      name: params.name ?? undefined,
      barcode: params.barcode ?? undefined,
      sourceId: params.sourceId ?? undefined,
      parentStorableId: params.parentStorableId ?? undefined,
      columnMappings: params.columnMappings?.length ? params.columnMappings : undefined,
      plateFieldMappings: params.plateFieldMappings ?? [],
      studyIds: params.studyIds ?? undefined,
    },
  });
}

// -- Bulk put in inventory ----------------------------------------------------

/**
 * Properties shared across every plate in a bulk put-in-inventory request. The
 * modal collects these once (schema, location, project, barcode mode, column
 * mappings) and the server merges them into each item that does not override
 * the field — see `_merge_put_in_inventory_defaults` on the backend.
 */
export interface BulkPutScratchPlateInInventoryDefaults {
  plateSchemaId?: string;
  barcode?: BarcodeInput;
  sourceId?: string;
  parentStorableId?: string;
  columnMappings?: Array<ScratchPlateColumnMapping>;
  plateFieldMappings?: Array<ScratchPlatePlateFieldMapping>;
  studyIds?: Array<string>;
}

/**
 * A single plate in a bulk request. `scratchPlateId` is required; every other
 * field overrides the request-level default for just this plate. In practice
 * the modal only varies `name` per plate.
 */
export interface BulkPutScratchPlateInInventoryItem extends BulkPutScratchPlateInInventoryDefaults {
  scratchPlateId: string;
  name?: string;
}

export interface BulkPutScratchPlateInInventoryParams {
  defaults?: BulkPutScratchPlateInInventoryDefaults;
  items: Array<BulkPutScratchPlateInInventoryItem>;
}

export interface BulkPutScratchPlateInInventoryTask {
  taskId: string;
  /** Session-authed URL to poll for status; see `getBulkPutInInventoryStatus`. */
  pollingUri: string;
}

export enum BulkTaskStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface BulkPutInInventoryStatus {
  taskId: string;
  status: BulkTaskStatus;
  progress: {totalRecords: number; numberProcessed: number};
  result: {successCount: number; errorCount: number};
}

/**
 * Kick off an async, batched put-in-inventory for many scratch plates. Unlike
 * the single-item `putScratchPlateInInventory`, this returns immediately with a
 * task to poll via `getBulkPutInInventoryStatus`.
 */
export function bulkPutScratchPlatesInInventory(
  params: BulkPutScratchPlateInInventoryParams
): Promise<BulkPutScratchPlateInInventoryTask> {
  return postJSONPromise({
    url: `${API_BASE}:bulk-put-in-inventory`,
    data: {defaults: params.defaults, items: params.items},
  });
}

/**
 * Poll the status of a bulk put-in-inventory task. Hits the session-authed
 * wrapper route (not the v3 `pollingUri`, which requires API-key auth the
 * webapp does not carry).
 */
export function getBulkPutInInventoryStatus(taskId: string): Promise<BulkPutInInventoryStatus> {
  return getJSONPromise(`${API_BASE}:bulk-put-in-inventory-status/${taskId}`);
}

// -- List mappable columns ----------------------------------------------------

export interface MappableColumn {
  systemName: string;
  displayName: string;
  leafType: string | null;
}

export interface MappableLayer {
  layerName: string;
  columns: Array<MappableColumn>;
}

export interface ListMappableColumnsResponse {
  layers: Array<MappableLayer>;
}

/** Fetch the columns each layer of a scratch plate exposes for column mapping. */
export function listScratchPlateMappableColumns(
  scratchPlateId: string
): Promise<ListMappableColumnsResponse> {
  return postJSONPromise({
    url: `${API_BASE}:list-mappable-columns`,
    data: {scratchPlateId},
  });
}
